import type { Promotion } from '@promotion/types'
import { Client as GameCommsClient, type PromotionClosedPayload } from '@spilnu/game-comms'
import { promotionCookie } from '@promotion/utils/promotionCookie'
import { ENGAGE_GAMES } from '@promotion/utils/engageGames'
import { navigateTo, useApi, usePromotionLogger, useState, useGame, useRewardChecks, useApm, useCoreConfig, useUserInfo, useQuery, type MaybeRefOrGetter, toValue, useMutation, computed, useQueryClient, usePromotionErrorMessage, whenever, useI18n, toError, usePromotionClient } from '#imports'
import type { PromotionClient, UseQueryFunctionOptions } from '@core/types'

type PendingPlayerOfferData = PromotionClient.components['schemas']['PendingPlayerOfferData']
type AcceptedOfferData = PromotionClient.components['schemas']['AcceptedOfferData']
type ReminderData = PromotionClient.components['schemas']['ReminderData']

export interface ContinueBonusBody {
    bonusType: PromotionClient.BonusType
    playerBonusId: string
}

export interface UseRewardsOptions {
    closeCallback?: any
}

export const useRewards = (options: UseRewardsOptions = {}) => {
    const { t } = useI18n()
    const errorMessage = usePromotionErrorMessage()
    const { site } = useCoreConfig()
    const { start } = useGame()
    const promoCookie = promotionCookie('promotionCookie')
    const logger = usePromotionLogger().withTag('useRewards')
    const promotionApi = useApi({ service: 'promotion' })
    const queryClient = useQueryClient()
    const { client: promotionClient } = usePromotionClient()

    const {
        isBingoTickets,
        isBonusCash,
        isBonusSpins,
        isScratchCards,
        isWheelOfFortuneSpins,
    } = useRewardChecks()

    const apmAgent = useApm()

    const iframeUrl = useState<string | null>('promotion:iframeurl', () => null)
    const isPromotionLoading = useState<boolean>('promotion:iframe:loading', () => false)
    const gameHubOpens = useState<number>('promotion:gamehub:opens', () => 0)
    const gameHubOwner = useState<number | null>('promotion:gamehub:owner', () => null)
    const gameReadsInFlight = useState<number>('promotion:gamehub:reads', () => 0)

    whenever(iframeUrl, () => {
        isPromotionLoading.value = true
    })

    const {
        closeCallback = null,
    } = options

    function handlePromotionCookie(value: string) {
        if (!value) return

        if (promoCookie.value) {
            promoCookie.value.push(value)

            return
        }

        promoCookie.value = [value]
    }

    function useGetPendingReward(options: Omit<UseQueryFunctionOptions<PendingPlayerOfferData | null>, 'key'> = {}) {
        const { enabled, ...queryOptions } = options

        const { canUsePlayerFeatures, status: userInfoStatus } = useUserInfo()

        return useQuery(async ({ signal }): Promise<PendingPlayerOfferData | null> => {
            const { data } = await promotionClient.GET('/v1/public/player-offers/pending', {
                signal,
                // site.brand may be the 'unknown' fallback; cast to the nominal Brand enum.
                params: { query: { brand: site.brand as PromotionClient.Brand } }
            })

            return !Array.isArray(data) && data?.acceptUrl ? data : null
        }, {
            server: false,
            lazy: true,
            ...queryOptions,
            queryCache: {
                ttl: 2 // Reduce TTL to ensure same promotion won't be fetched twice
            },
            key: 'promotions:rewards:pending',
            enabled: () => {
                if (!canUsePlayerFeatures.value || userInfoStatus.value === 'pending') return false
                // Pause while a scratchcard/wheel iframe is open or being read — closing the
                // promotion modal clears this query, and refetching before the
                // gamehub closed event would pop the next promotion mid-game
                if (iframeUrl.value || gameReadsInFlight.value) return false

                return toValue(options.enabled ?? true)
            }
        })
    }

    function reminderKey(reminder: ReminderData) {
        return reminder.playerBonus?.id ?? reminder.title
    }

    function useGetReminders(options: Omit<UseQueryFunctionOptions<ReminderData[] | undefined>, 'key'> = {}) {
        const { enabled = true, ...queryOptions } = options

        const { canUsePlayerFeatures, status: userInfoStatus } = useUserInfo()
        const eligible = () => canUsePlayerFeatures.value && userInfoStatus.value !== 'pending'
        const active = () => eligible() && toValue(enabled)

        const query = useQuery(async ({ signal }): Promise<ReminderData[] | undefined> => {
            // Every consumer of the key shares this handler, so only the player's eligibility may skip it.
            if (!eligible()) return undefined

            const { data } = await promotionClient.GET('/v1/public/player-offers/reminders', {
                signal,
                params: { query: { brand: site.brand as PromotionClient.Brand } }
            })

            return data?.reminders ?? []
        }, {
            server: false,
            lazy: true,
            ...queryOptions,
            key: 'promotions:rewards:reminders',
            enabled: active,
        })

        async function refresh(...args: Parameters<typeof query.refresh>) {
            if (active()) await query.refresh(...args)
        }

        function close(reminder: ReminderData) {
            handlePromotionCookie(reminderKey(reminder))

            query.data.value = query.data.value?.filter(r => r !== reminder)
        }

        const data = computed(() => query.data.value
            ?.filter(reminder => !promoCookie.value?.includes(reminderKey(reminder)))
            .slice(0, 3))

        return query.enrich({
            data,
            refresh,
            close
        })
    }

    function getRewardImgPath(reward: Promotion, activeState = false) {
        if (reward.reward_image_path) {
            return reward.reward_image_path
        }

        let imgPath = '/theme/Spilnu/img/reward/'

        switch (true) {
            case isBingoTickets(reward):
                imgPath += 'bingo-tickets.png'
                break
            case isBonusSpins(reward):
                imgPath += 'bonus-spins.png'
                break
            case isScratchCards(reward):
                imgPath += 'scratchcards.png'
                break
            case isWheelOfFortuneSpins(reward):
                imgPath += 'wheel-of-fortune.png'
                break
            case isBonusCash(reward):
                imgPath += 'bonus-cash.png'
                break
            default:
                if (activeState) {
                    imgPath += 'present_open.png'
                }
                else {
                    imgPath += 'present.png'
                }
        }

        return imgPath
    }

    function openEngageGame(bonusType: PromotionClient.BonusType, query: 'entry_hash' | 'prize_draw_id', value: string) {
        // Hub clients never unsubscribe, so only the open the iframe shows, until its close, may act.
        const open = ++gameHubOpens.value
        const isCurrent = () => gameHubOwner.value === open
        const { path, channel, topic } = ENGAGE_GAMES[bonusType]!

        try {
            const gameHubClient = new GameCommsClient({
                channel: 'page',
                group: 'promotions',
            })

            gameHubClient.subscribeTo(channel, topic.ready, () => {
                if (!isCurrent()) return

                isPromotionLoading.value = false
            })

            gameHubClient.subscribeTo(channel, topic.closed, async (payload: PromotionClosedPayload) => {
                if (!isCurrent()) return

                gameHubOwner.value = null
                iframeUrl.value = null

                // Saved game_id as fallback since scratchcard still sends this as slug -_-
                const gameSlug = payload.slug || payload.game_id

                if (gameSlug) {
                    await start({ gameSlug })
                }

                queryClient.invalidateQueries({
                    key: 'promotions:rewards'
                })
            })
        }
        catch (error) {
            logger.error('openEngageGame failed with error: ', JSON.stringify(error))

            apmAgent.captureError(toError(error))
        }

        gameHubOwner.value = open
        iframeUrl.value = `${path}?${query}=${value}`
    }

    async function openPlayerBonusGame({ bonusType, playerBonusId }: ContinueBonusBody) {
        gameReadsInFlight.value++

        try {
            const { data } = await promotionClient.GET('/v1/public/prize-draws', {
                params: { query: { playerBonusId } }
            })

            if (data?.prizeDraw) return openEngageGame(bonusType, 'prize_draw_id', playerBonusId)
            if (data?.engage?.entryHash) return openEngageGame(bonusType, 'entry_hash', data.engage.entryHash)

            throw new Error(`Player bonus ${playerBonusId} has no game to open`)
        }
        finally {
            gameReadsInFlight.value--
        }
    }

    function useContinueBonus() {
        return useMutation(async (body: ContinueBonusBody) => {
            if (!ENGAGE_GAMES[body.bonusType]) throw new Error(`A ${body.bonusType} bonus has no Engage game to continue`)

            await openPlayerBonusGame(body)
        }, { mode: 'leading' })
    }

    function useAcceptOffer(offer: MaybeRefOrGetter<PendingPlayerOfferData | null | undefined>) {
        const continueBonus = useContinueBonus()
        const mutation = useMutation(async () => {
            const offerRaw = toValue(offer)

            if (!offerRaw) return

            return promotionApi<AcceptedOfferData>(offerRaw.acceptUrl, { method: 'PUT' })
        }, {
            async onSuccess(data) {
                if (!data) throw new Error('No data present')

                // The modal closes an enqueued offer itself, with the enqueued message.
                if (data.state === 'ENQUEUED') return

                // The sheet lives in the layout, so it would stay open over the deposit page with the offer already accepted.
                if (toValue(offer)?.hasPaymentRequirement) {
                    closeCallback?.(navigateTo, { path: t('pages.deposit'), query: { claimingBonus: 'true' } })
                    return
                }

                if (data.engage && ENGAGE_GAMES[data.bonusType]) {
                    const { entryHash } = data.engage
                    closeCallback?.(() => openEngageGame(data.bonusType, 'entry_hash', entryHash))
                    return
                }

                const { bonusType, playerBonusId } = data

                if (playerBonusId && ENGAGE_GAMES[bonusType]) {
                    closeCallback?.(() => continueBonus.mutate({ bonusType, playerBonusId }))
                    return
                }

                const [game] = data.games

                if (game && data.state !== 'PENDING_REQUIREMENTS') {
                    closeCallback?.(start, {
                        gameSlug: game.slug,
                        ticketsReceived: data.bonusType === 'BINGO_TICKETS' ? Number(data.amount) : 0,
                    })
                    return
                }

                closeCallback?.('next')
            }
        })

        const buttonText = computed(() => {
            const offerRaw = toValue(offer)

            if (mutation.pending.value) return `${t('promotions.loading')}...`
            if (offerRaw?.ctaText) return offerRaw.ctaText
            if (offerRaw?.hasPaymentRequirement) return t('promotions.yesPlease')

            switch (offerRaw?.rewardType) {
                case 'SCRATCHCARDS': return t('promotions.scratchNow')
                case 'WHEEL_OF_FORTUNE': return t('promotions.spinNow')
                case 'SPINS':
                case 'DROP_THE_BALL': return t('promotions.playNow')
                case 'BINGO_TICKETS': return t('promotions.playBingoNow')
                default: return t('promotions.yesPlease')
            }
        })

        const messages = computed(() => {
            const _messages: {
                save?: string
                error?: string
            } = {}

            if (mutation.data.value?.state === 'ENQUEUED') {
                _messages.save = t('promotions.enqueuedMessage')
            }

            if (mutation.error.value) {
                _messages.error = errorMessage(mutation.error.value)
            }

            return _messages
        })

        return {
            ...mutation,
            buttonText,
            messages
        }
    }

    function useDeclinePromotion(offer: MaybeRefOrGetter<PendingPlayerOfferData | null | undefined>) {
        const mutation = useMutation(async () => {
            const offerRaw = toValue(offer)

            if (!offerRaw) return

            return promotionApi(offerRaw.declineUrl, { method: 'PUT' })
        })

        const messages = computed(() => {
            const _messages: {
                save?: string
                error?: string
            } = {}

            if (mutation.isSuccess.value) {
                _messages.save = t('promotions.saveMessage')
            }

            if (mutation.error.value) {
                _messages.error = errorMessage(mutation.error.value)
            }

            return _messages
        })

        return {
            ...mutation,
            messages
        }
    }

    return {
        iframeUrl,
        isPromotionLoading,

        getRewardImgPath,
        handlePromotionCookie,

        useGetPendingReward,
        useGetReminders,
        useContinueBonus,
        useAcceptOffer,
        useDeclinePromotion
    }
}
